import {
  DevicesDeviceCommand,
  useListDevices,
  useExecuteCommand,
  useUpdateDeviceState,
  useDeleteDevice,
  useGetApiVirtualDevices,
  useDeleteApiVirtualDevicesId,
  getListDevicesQueryKey,
  getGetDeviceQueryKey,
  getGetApiVirtualDevicesQueryKey,
  type DevicesDeviceType,
  type DevicesDeviceSubtype,
  type DevicesDevice,
  type DevicesDeviceState,
  type DevicesAvailabilityState,
  type VirtualdevicesVirtualDeviceDTO,
} from '@smarthome/api';
import { useLocalStorage, useDeviceStream, type DeviceStreamEvent, ROUTES } from '@smarthome/ui';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw, AlertCircle, Cpu, Star, X, Settings } from 'lucide-react';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { SearchField, Select, type SelectOption } from '@/components/ui';
import {
  DeviceControlModal,
  DeviceList,
  applyStatePatch,
  buildOnOffStatePatch,
  filterAndSortDevices,
  getDeviceIdForApi,
  getUniqueProviders,
  getUniqueTypes,
  getDeviceTypeLabel,
  SORT_OPTIONS,
  type SortOption,
} from '@/features/devices';
import { VirtualDeviceModal } from '@/features/virtual-devices';

type FilterMode = 'favorites' | 'all';
const DEVICE_CONTROL_PARAM = 'device';

export function DevicesPage() {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [typeFilter, setTypeFilter] = useState<DevicesDeviceType | 'all'>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [filterMode, setFilterMode] = useLocalStorage<FilterMode>(
    'devices-filter-mode',
    'favorites'
  );
  const [selectedDevice, setSelectedDevice] = useState<DevicesDevice | null>(null);
  const [pendingPowerDeviceIds, setPendingPowerDeviceIds] = useState<Set<string>>(new Set());
  const [showVirtualDeviceModal, setShowVirtualDeviceModal] = useState(false);
  const [virtualDeviceToEdit, setVirtualDeviceToEdit] =
    useState<VirtualdevicesVirtualDeviceDTO | null>(null);
  const [virtualDeviceToDelete, setVirtualDeviceToDelete] = useState<DevicesDevice | null>(null);
  const [deviceToDelete, setDeviceToDelete] = useState<DevicesDevice | null>(null);
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data, isLoading, isError, refetch, isFetching } = useListDevices({
    query: {
      refetchInterval: 15000,
      refetchOnWindowFocus: true,
    },
  });

  // Get virtual devices list to look up config when editing
  const { data: virtualDevicesData } = useGetApiVirtualDevices();
  const virtualDevices = useMemo(
    () => virtualDevicesData?.virtualDevices ?? [],
    [virtualDevicesData?.virtualDevices]
  );

  // Delete virtual device mutation
  const deleteMutation = useDeleteApiVirtualDevicesId({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListDevicesQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetApiVirtualDevicesQueryKey() });
        setVirtualDeviceToDelete(null);
      },
    },
  });

  // Delete device mutation
  const deleteDeviceMutation = useDeleteDevice({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListDevicesQueryKey() });
        setDeviceToDelete(null);
      },
    },
  });

  const { mutate: updateDeviceState } = useUpdateDeviceState({
    mutation: {
      onMutate: async ({ id, data: stateData }) => {
        // Mark device as pending
        setPendingPowerDeviceIds((prev) => new Set(prev).add(id));

        await queryClient.cancelQueries({ queryKey: getListDevicesQueryKey() });

        const previousData = queryClient.getQueryData<{ devices: DevicesDevice[] }>(
          getListDevicesQueryKey()
        );

        // Optimistic update for list view
        queryClient.setQueryData<{ devices: DevicesDevice[] }>(getListDevicesQueryKey(), (old) => {
          if (!old?.devices) return old;
          return {
            ...old,
            devices: old.devices.map((device) => {
              // Match by id or humanId since API accepts both
              if (device.id === id || device.humanId === id) {
                return {
                  ...device,
                  state: applyStatePatch(device.state, stateData.state),
                };
              }
              return device;
            }),
          };
        });

        // Also update device detail cache if present
        const detailKey = getGetDeviceQueryKey(id);
        const previousDetail = queryClient.getQueryData<{ device: DevicesDevice }>(detailKey);
        if (previousDetail) {
          queryClient.setQueryData<{ device: DevicesDevice }>(detailKey, (old) => {
            if (!old?.device) return old;
            return {
              ...old,
              device: {
                ...old.device,
                state: applyStatePatch(old.device.state, stateData.state),
              },
            };
          });
        }

        return { previousData, previousDetail, deviceId: id };
      },
      onError: (_err, { id }, context) => {
        // Rollback on error
        if (context?.previousData) {
          queryClient.setQueryData(getListDevicesQueryKey(), context.previousData);
        }
        if (context?.previousDetail) {
          queryClient.setQueryData(getGetDeviceQueryKey(id), context.previousDetail);
        }
      },
      onSettled: (_data, _err, { id }) => {
        // Clear pending state
        setPendingPowerDeviceIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        // Invalidate to ensure fresh data
        queryClient.invalidateQueries({ queryKey: getGetDeviceQueryKey(id) });
        queryClient.invalidateQueries({ queryKey: getListDevicesQueryKey() });
      },
    },
  });

  const { mutate: executeCommand } = useExecuteCommand();

  // Handle real-time device events
  const handleDeviceEvent = useCallback(
    (event: DeviceStreamEvent) => {
      if (event.type === 'device_state_changed' || event.type === 'device_availability_changed') {
        // Cast stream event types to API types
        const newState = event.state as DevicesDeviceState | undefined;
        const newAvailability = event.availability as DevicesAvailabilityState | undefined;
        const newSubtype = event.deviceSubtype as DevicesDeviceSubtype | undefined;

        // Update the device in the cache directly for instant UI update
        queryClient.setQueryData<{ devices: DevicesDevice[] }>(
          getListDevicesQueryKey(),
          (oldData) => {
            if (!oldData?.devices) return oldData;
            return {
              ...oldData,
              devices: oldData.devices.map((device) => {
                if (device.id === event.deviceId || device.humanId === event.humanId) {
                  return {
                    ...device,
                    state: newState ?? device.state,
                    availability: newAvailability ?? device.availability,
                    subtype: newSubtype ?? device.subtype,
                  };
                }
                return device;
              }),
            };
          }
        );

        // Also update device detail cache if present
        const detailKey = getGetDeviceQueryKey(event.deviceId);
        queryClient.setQueryData<{ device: DevicesDevice }>(detailKey, (oldData) => {
          if (!oldData?.device) return oldData;
          return {
            ...oldData,
            device: {
              ...oldData.device,
              state: newState ?? oldData.device.state,
              availability: newAvailability ?? oldData.device.availability,
              subtype: newSubtype ?? oldData.device.subtype,
            },
          };
        });
      } else if (event.type === 'device_registered' || event.type === 'device_unregistered') {
        // For registration/unregistration, just invalidate to refetch
        queryClient.invalidateQueries({ queryKey: getListDevicesQueryKey() });
      }
    },
    [queryClient]
  );

  // Subscribe to real-time device updates
  useDeviceStream({
    enabled: true,
    onEvent: handleDeviceEvent,
  });

  const handleTogglePower = useCallback(
    (device: DevicesDevice, nextOn: boolean) => {
      const id = getDeviceIdForApi(device);
      if (!id) return;

      const statePatch = buildOnOffStatePatch(device, nextOn);
      updateDeviceState({ id, data: { state: statePatch } });
    },
    [updateDeviceState]
  );

  const handlePositionChange = useCallback(
    (device: DevicesDevice, positionId: string) => {
      const id = getDeviceIdForApi(device);
      if (!id) return;

      updateDeviceState({ id, data: { state: { switch: { position: positionId } } } });
    },
    [updateDeviceState]
  );

  const handleStopWindowCovering = useCallback(
    (device: DevicesDevice) => {
      const id = getDeviceIdForApi(device);
      if (!id) return;
      executeCommand({ id, data: { command: DevicesDeviceCommand.DeviceCommandStop } });
    },
    [executeCommand]
  );

  const handleOpenControl = useCallback(
    (device: DevicesDevice) => {
      setSelectedDevice(device);
      const deviceParam = device.humanId || device.id;
      if (!deviceParam) return;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(DEVICE_CONTROL_PARAM, deviceParam);
        return next;
      });
    },
    [setSearchParams]
  );

  const handleCloseControl = useCallback(() => {
    setSelectedDevice(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(DEVICE_CONTROL_PARAM);
      return next;
    });
  }, [setSearchParams]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  // Virtual device handlers
  const handleEditVirtualDevice = useCallback(
    (device: DevicesDevice) => {
      // Find the matching virtual device DTO from the virtual devices list by humanId
      const virtualDevice = virtualDevices.find((vd) => vd.humanId === device.humanId);
      if (virtualDevice) {
        setVirtualDeviceToEdit(virtualDevice);
        setShowVirtualDeviceModal(true);
      } else {
        console.warn(
          '[DevicesPage] Could not find virtual device to edit.',
          'device.humanId:',
          device.humanId,
          'virtualDevices count:',
          virtualDevices.length,
          'available humanIds:',
          virtualDevices.map((vd) => vd.humanId)
        );
      }
    },
    [virtualDevices]
  );

  const handleDeleteVirtualDevice = useCallback((device: DevicesDevice) => {
    setVirtualDeviceToDelete(device);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!virtualDeviceToDelete) return;
    // Find the virtual device by humanId
    const virtualDevice = virtualDevices.find((vd) => vd.humanId === virtualDeviceToDelete.humanId);
    if (virtualDevice?.id) {
      deleteMutation.mutate({ id: virtualDevice.id });
    } else {
      console.warn(
        '[DevicesPage] Could not find virtual device to delete.',
        'device.humanId:',
        virtualDeviceToDelete.humanId,
        'virtualDevices count:',
        virtualDevices.length,
        'available humanIds:',
        virtualDevices.map((vd) => vd.humanId)
      );
    }
  }, [virtualDeviceToDelete, virtualDevices, deleteMutation]);

  // Device deletion handlers
  const handleDeleteDevice = useCallback((device: DevicesDevice) => {
    setDeviceToDelete(device);
  }, []);

  const handleConfirmDeleteDevice = useCallback(() => {
    if (!deviceToDelete) return;
    const deviceId = getDeviceIdForApi(deviceToDelete);
    if (deviceId) {
      deleteDeviceMutation.mutate({ id: deviceId });
    }
  }, [deviceToDelete, deleteDeviceMutation]);

  const handleCloseVirtualDeviceModal = useCallback(() => {
    setShowVirtualDeviceModal(false);
    setVirtualDeviceToEdit(null);
  }, []);

  const devices = useMemo(() => data?.devices || [], [data?.devices]);
  const deviceParam = searchParams.get(DEVICE_CONTROL_PARAM);

  const deviceFromParam = useMemo(() => {
    if (!deviceParam) return null;
    return (
      devices.find((device) => device.humanId === deviceParam || device.id === deviceParam) ?? null
    );
  }, [deviceParam, devices]);

  useEffect(() => {
    if (!deviceParam) {
      if (selectedDevice) {
        setSelectedDevice(null);
      }
      return;
    }
    if (!deviceFromParam) return;
    if (
      !selectedDevice ||
      (selectedDevice.humanId !== deviceFromParam.humanId &&
        selectedDevice.id !== deviceFromParam.id)
    ) {
      setSelectedDevice(deviceFromParam);
    }
  }, [deviceParam, deviceFromParam, selectedDevice]);

  const uniqueTypes = useMemo(() => getUniqueTypes(devices), [devices]);
  const uniqueProviders = useMemo(() => getUniqueProviders(devices), [devices]);

  // Build select options
  const typeOptions: SelectOption[] = useMemo(() => {
    return [
      { value: 'all', label: 'All types' },
      ...uniqueTypes.map((type) => ({ value: type, label: getDeviceTypeLabel(type) })),
    ];
  }, [uniqueTypes]);

  const providerOptions: SelectOption[] = useMemo(() => {
    return [
      { value: 'all', label: 'All providers' },
      ...uniqueProviders.map((provider) => ({ value: provider, label: provider })),
    ];
  }, [uniqueProviders]);

  const sortOptions: SelectOption[] = useMemo(() => {
    return SORT_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }));
  }, []);

  const filteredDevices = useMemo(() => {
    return filterAndSortDevices(devices, {
      search,
      type: typeFilter,
      provider: providerFilter,
      favoritesOnly: filterMode === 'favorites',
      sortBy,
    });
  }, [devices, search, typeFilter, providerFilter, filterMode, sortBy]);

  const favoritesCount = useMemo(() => devices.filter((d) => d.isFavorite).length, [devices]);
  const availableCount = useMemo(
    () => devices.filter((d) => d.availability === 'available').length,
    [devices]
  );

  // Get the current device data from the cache for the modal
  // (ensures modal shows latest state after optimistic updates)
  const currentSelectedDevice = useMemo(() => {
    if (!selectedDevice) return null;
    const deviceInList = devices.find(
      (d) => d.id === selectedDevice.id || d.humanId === selectedDevice.humanId
    );
    return deviceInList || selectedDevice;
  }, [selectedDevice, devices]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-shrink-0 items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Devices
          </h1>
          <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
            {isLoading
              ? 'Loading...'
              : `${devices.length} device${devices.length !== 1 ? 's' : ''} · ${availableCount} available`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={ROUTES.VIRTUAL_DEVICES}
            className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition-all hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800"
          >
            <Settings className="h-4 w-4" />
            Virtual Devices
          </Link>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900 transition-all hover:border-gray-300 hover:bg-gray-50 disabled:opacity-50 dark:border-white/[0.06] dark:bg-[#18181b] dark:text-white dark:hover:border-white/[0.1] dark:hover:bg-[#1f1f23]"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-shrink-0 flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-4">
          {/* Favorites / All toggle */}
          <div className="flex rounded-lg border border-gray-200 bg-white p-0.5 dark:border-white/[0.06] dark:bg-[#18181b]">
            <button
              type="button"
              onClick={() => setFilterMode('favorites')}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                filterMode === 'favorites'
                  ? 'bg-gray-100 text-gray-900 dark:bg-white/[0.08] dark:text-white'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-zinc-400 dark:hover:bg-white/[0.04]'
              }`}
            >
              <Star
                className="h-3.5 w-3.5"
                fill={filterMode === 'favorites' ? 'currentColor' : 'none'}
              />
              Favorites
              {favoritesCount > 0 && (
                <span className="text-xs text-gray-500 dark:text-zinc-500">({favoritesCount})</span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setFilterMode('all')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                filterMode === 'all'
                  ? 'bg-gray-100 text-gray-900 dark:bg-white/[0.08] dark:text-white'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-zinc-400 dark:hover:bg-white/[0.04]'
              }`}
            >
              All
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="w-full sm:w-56">
            <SearchField
              label="Search"
              hideLabel
              placeholder="Search devices..."
              value={search}
              onChange={handleSearchChange}
            />
          </div>

          <Select
            label="Type"
            hideLabel
            value={typeFilter}
            onChange={(v) => setTypeFilter(v as DevicesDeviceType | 'all')}
            options={typeOptions}
          />

          <Select
            label="Provider"
            hideLabel
            value={providerFilter}
            onChange={setProviderFilter}
            options={providerOptions}
          />

          <Select
            label="Sort"
            hideLabel
            value={sortBy}
            onChange={(v) => setSortBy(v as SortOption)}
            options={sortOptions}
          />
        </div>
      </div>

      {/* Device list */}
      <div className="min-h-48 overflow-visible rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
          </div>
        ) : isError ? (
          <div className="p-6 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10">
              <AlertCircle className="h-6 w-6 text-red-400" />
            </div>
            <p className="text-sm text-gray-600 dark:text-zinc-400">Failed to load devices</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-3 text-sm text-blue-500 hover:text-blue-400"
            >
              Try again
            </button>
          </div>
        ) : filteredDevices.length === 0 ? (
          <div className="p-6 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-zinc-800">
              {filterMode === 'favorites' ? (
                <Star className="h-6 w-6 text-gray-500 dark:text-zinc-500" fill="none" />
              ) : (
                <Cpu className="h-6 w-6 text-gray-500 dark:text-zinc-500" />
              )}
            </div>
            <p className="text-sm text-gray-600 dark:text-zinc-400">
              {search || typeFilter !== 'all' || providerFilter !== 'all'
                ? 'No devices match your filters'
                : filterMode === 'favorites'
                  ? 'No favorite devices yet'
                  : 'No devices configured'}
            </p>
            {search || typeFilter !== 'all' || providerFilter !== 'all' ? (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setTypeFilter('all');
                  setProviderFilter('all');
                }}
                className="mt-3 text-sm text-blue-500 hover:text-blue-400"
              >
                Clear filters
              </button>
            ) : filterMode === 'favorites' ? (
              <button
                type="button"
                onClick={() => setFilterMode('all')}
                className="mt-3 text-sm text-blue-500 hover:text-blue-400"
              >
                View all devices
              </button>
            ) : null}
          </div>
        ) : (
          <DeviceList
            devices={filteredDevices}
            onTogglePower={handleTogglePower}
            onStopWindowCovering={handleStopWindowCovering}
            onPositionChange={handlePositionChange}
            onOpenControl={handleOpenControl}
            onDelete={handleDeleteDevice}
            pendingPowerDeviceIds={pendingPowerDeviceIds}
          />
        )}
      </div>

      {/* Device Control Modal */}
      {currentSelectedDevice && (
        <DeviceControlModal
          device={currentSelectedDevice}
          isOpen={!!currentSelectedDevice}
          onClose={handleCloseControl}
          onEditVirtualDevice={() => {
            handleEditVirtualDevice(currentSelectedDevice);
            handleCloseControl();
          }}
          onDeleteVirtualDevice={() => {
            handleDeleteVirtualDevice(currentSelectedDevice);
            handleCloseControl();
          }}
        />
      )}

      {/* Virtual Device Modal (Create/Edit) */}
      <VirtualDeviceModal
        isOpen={showVirtualDeviceModal}
        onClose={handleCloseVirtualDeviceModal}
        virtualDevice={virtualDeviceToEdit ?? undefined}
      />

      {/* Delete Virtual Device Confirmation */}
      {virtualDeviceToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl dark:border-white/[0.06] dark:bg-[#18181b]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Delete Virtual Device
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-zinc-500">
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setVirtualDeviceToDelete(null)}
                className="text-gray-500 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="mt-4 text-sm text-gray-600 dark:text-zinc-400">
              Are you sure you want to delete{' '}
              <span className="font-medium text-gray-900 dark:text-white">
                {virtualDeviceToDelete.name ?? virtualDeviceToDelete.humanId}
              </span>
              ? This will also remove the associated device from the system.
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setVirtualDeviceToDelete(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-white/[0.06] dark:text-zinc-300 dark:hover:bg-white/[0.03]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deleteMutation.isPending}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending && (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Device Confirmation */}
      {deviceToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl dark:border-white/[0.06] dark:bg-[#18181b]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Delete Device
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-zinc-500">
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setDeviceToDelete(null)}
                className="text-gray-500 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="mt-4 text-sm text-gray-600 dark:text-zinc-400">
              Are you sure you want to delete{' '}
              <span className="font-medium text-gray-900 dark:text-white">
                {deviceToDelete.name ?? deviceToDelete.humanId}
              </span>
              ? This will remove the device from the system.
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeviceToDelete(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-white/[0.06] dark:text-zinc-300 dark:hover:bg-white/[0.03]"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeleteDevice}
                disabled={deleteDeviceMutation.isPending}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
              >
                {deleteDeviceMutation.isPending && (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
